import React from 'react';
import { useMany, useTranslate, useGetLocale, useDeleteMany } from "@pankod/refine-core";

import {
  Input,
  InputNumber,
  useTable,
  List,
  Table,
  DateField,
  Select,
  useSelect,
  TextField,
  FilterDropdown,
  ShowButton,
  Space,
  EditButton,
  DeleteButton,
  Radio,
  getDefaultSortOrder,
  BooleanField,
  Button,
  CreateButton,
  useModal,
  Modal,
  Checkbox,
  Typography,
  Icons,
  getDefaultFilter,
  Tag,
} from "@pankod/refine-antd";

import { IActivityVolume, IHospital } from 'interfaces';

export const VolumeList = () => {
  const translate = useTranslate();
  const defaultColumns = React.useMemo(
    () => [
      {
        label: translate('hospital_activity_volumes.fields.id'),
        value: 'id',
      },
      {
        label: translate('hospital_activity_volumes.fields.year'),
        value: 'year',
      },
      {
        label: translate('hospital_activity_volumes.fields.surgeries_performed'),
        value: 'surgeries_performed',
      },
      {
        label: translate('hospital_activity_volumes.fields.hospital.title'),
        value: 'hospital_id',
      },
      {
        label: translate('hospital_activity_volumes.fields.created_at'),
        value: 'created_at',
      },
      {
        label: translate('hospital_activity_volumes.fields.updated_at'),
        value: 'updated_at',
      },
    ],
    [],
  );
  const { modalProps: columnsModalProps, show: columnsShow } = useModal();
  const [hiddenColumns, setHiddenColumns] = React.useState<string[]>(['created_at', 'updated_at', 'id']);
  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
  const { tableProps, sorter, tableQueryResult } = useTable<IActivityVolume>({
    initialSorter: [
      {
        field: 'id',
        order: 'asc',
      },
      {
        field: 'hospital_id',
        order: 'asc',
      },
    ],
  });
  const hospitalIds = tableProps?.dataSource?.map(item => item.hospital_id) ?? [];
  const { data: hospitalData, isLoading } = useMany<IHospital>({
    resource: 'hospitals',
    ids: hospitalIds,
    queryOptions: {
      enabled: hospitalIds.length > 0,
    },
  });
  const { selectProps: hospitalSelectProps } = useSelect<IHospital>({
    resource: 'hospitals',
    optionLabel: 'denomination',
    optionValue: 'id',
  });
  const { mutate, isSuccess, isLoading: deleteManyIsLoading } = useDeleteMany<IActivityVolume>();
  const deleteSelectedItems = () => {
    mutate({
      resource: 'hospital_activity_volumes',
      ids: selectedRowKeys.map(String),
    });
  };
  const onSelectChange = (selectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(selectedRowKeys);
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [Table.SELECTION_ALL, Table.SELECTION_INVERT, Table.SELECTION_NONE],
  };
  const hasSelected = selectedRowKeys.length > 0;
  const handleHideColumn = (value: string, isChecked: boolean) => {
    if (isChecked) setHiddenColumns(prev => prev.filter(col => col !== value));
    else setHiddenColumns(prev => [...prev, value]);
  };
  const columns = React.useMemo(
    () => [
      <Table.Column
        key='id'
        dataIndex='id'
        title={translate('hospital_activity_volumes.fields.id')}
        sorter
        defaultSortOrder={getDefaultSortOrder('id', sorter)}
      />,
      <Table.Column
        key='year'
        dataIndex='year'
        title={translate('hospital_activity_volumes.fields.year')}
        filterDropdown={props => (
          <FilterDropdown {...props}>
            <InputNumber placeholder={translate('labels.search')} />
          </FilterDropdown>
        )}
      />,
      <Table.Column
        key='surgeries_performed'
        dataIndex='surgeries_performed'
        title={translate('hospital_activity_volumes.fields.surgeries_performed')}
        filterDropdown={props => (
          <FilterDropdown {...props}>
            <InputNumber placeholder={translate('labels.search')} />
          </FilterDropdown>
        )}
      />,
      <Table.Column
        key='hospital_id'
        dataIndex='hospital_id'
        title={translate('hospital_activity_volumes.fields.hospital.title')}
        render={(value: string) => {
          if (isLoading) {
            return <TextField value={translate('loading')} />;
          }
          return <TextField value={hospitalData?.data.find((item: IHospital) => item.id === value)?.denomination} />;
        }}
        filterDropdown={props => (
          <FilterDropdown {...props}>
            <Select
              mode='multiple'
              style={{ minWidth: 200 }}
              placeholder={translate('hospital_activity_volumes.fields.hospital.filters.placeholder')}
              {...hospitalSelectProps}
            />
          </FilterDropdown>
        )}
        defaultSortOrder={getDefaultSortOrder('hospital_id', sorter)}
      />,
      <Table.Column
        key='created_at'
        dataIndex='created_at'
        title={translate('hospital_activity_volumes.fields.created_at')}
        render={value => <DateField format='lll' value={value} />}
        sorter
      />,

      <Table.Column
        key='updated_at'
        dataIndex='updated_at'
        title={translate('hospital_activity_volumes.fields.updated_at')}
        render={value => <DateField format='lll' value={value} />}
        sorter
      />,
    ],
    [tableProps, sorter, hospitalSelectProps, isLoading, hospitalData],
  );
  const visibleColumns = React.useMemo(() => {
    return columns.filter(column => !hiddenColumns.includes(column.props.dataIndex));
  }, [hiddenColumns, columns]);

  React.useEffect(() => {
    if (isSuccess) {
      setSelectedRowKeys([]);
    }
  }, [isSuccess]);

  return (
    <>
      <List
        pageHeaderProps={{
          subTitle: hasSelected && (
            <Button danger onClick={deleteSelectedItems} disabled={!hasSelected} loading={deleteManyIsLoading}>
              {`Elimina ${selectedRowKeys.length} oggetti selezionati`}
            </Button>
          ),
          extra: (
            <Space>
              <Tag color='#f1c531'>
                {translate('labels.totalNumber', {
                  resource: translate('hospital_activity_volumes.hospital_activity_volumes').toLowerCase(),
                })}
                {tableQueryResult.data?.total || ''}
              </Tag>
              <CreateButton />
              <Button onClick={columnsShow}>{translate('table.hideColumns.buttonTitle')}</Button>
            </Space>
          ),
        }}>
        <Table<IActivityVolume>
          rowSelection={rowSelection}
          {...tableProps}
          size='small'
          scroll={{ y: '60vh', x: 10 * visibleColumns.length + 'vw' }}
          pagination={{ ...tableProps.pagination, position: ['topLeft'], size: 'small' }}
          rowKey='id'>
          <Table.Column<IActivityVolume>
            title=''
            dataIndex='actions'
            render={(_text, record): React.ReactNode => {
              return (
                <Space>
                  <ShowButton title={translate('buttons.show')} size='small' recordItemId={record.id} hideText />
                  <EditButton title={translate('buttons.edit')} size='small' recordItemId={record.id} hideText />
                  <DeleteButton title={translate('buttons.delete')} size='small' recordItemId={record.id} hideText />
                </Space>
              );
            }}
          />
          {visibleColumns}
        </Table>
      </List>
      <Modal footer={null} {...columnsModalProps}>
        <Space direction='vertical'>
          <Typography.Text>{translate('table.hideColumns.title')}</Typography.Text>
          {defaultColumns.map(column => (
            <Checkbox
              key={column.value}
              checked={!hiddenColumns.includes(column.value)}
              onChange={e => handleHideColumn(column.value, e.target.checked)}>
              {column.label}
            </Checkbox>
          ))}
        </Space>
      </Modal>
    </>
  );
};
